import type { DomainEntity, Entity } from '@backstage/catalog-model';
import type {
  GrafanaDashboard,
  ProductEntity,
  SolutionEntity,
  TemplateV1beta3,
} from '@ipen-portal/ipen-entity-model';

import {
  isDomainEntity,
  isRootDomainEntity,
} from '@ipen-portal/ipen-entity-model';
import { isObject } from 'es-toolkit/compat';

import type { EntityFilter, EntityFilterKeys } from './utils';

const hasSpec = (value: unknown): value is { spec: object } =>
  isObject(value) && 'spec' in value && isObject(value.spec);

/** @public */
export type IPenLogoEntity = Entity & {
  spec: {
    logo: string;
  };
};

export const isIPenLogoEntity = (value: unknown): value is IPenLogoEntity =>
  hasSpec(value) && 'logo' in value.spec;

/** @public */
export type IPenImageEntity = Entity & {
  spec: {
    image: string;
  };
};

export const isIPenImageEntity = (value: unknown): value is IPenImageEntity =>
  hasSpec(value) && 'image' in value.spec;

/** @public */
export type IPenBackgroundEntity = Entity & {
  spec: {
    background: string;
  };
};

export type Lifecycle<T extends Entity> = T extends {
  spec: { lifecycle?: infer L };
}
  ? L
  : never;

export type ProductLifecycle = Lifecycle<ProductEntity>;

export type SolutionLifecycle = Lifecycle<SolutionEntity>;

export const isIPenBackgroundEntity = (
  value: unknown,
): value is IPenBackgroundEntity =>
  hasSpec(value) && 'background' in value.spec;

export const isEntityWithGrafanaDashboards = (
  entity: unknown,
): entity is Entity & {
  spec: { grafanaDashboards: readonly GrafanaDashboard[] };
} =>
  hasSpec(entity)
  && 'grafanaDashboards' in entity.spec
  && Array.isArray(entity.spec.grafanaDashboards);

/** @public */
export type HasIPenSharedMails = Entity & {
  spec: {
    sharedMails?: {
      email: string;
      title: string;
    }[];
  };
};

/** @public */
export const isSharedMailsEntity = (
  value: unknown,
): value is HasIPenSharedMails => hasSpec(value) && 'sharedMails' in value.spec;

/** @public */
export type HasIPenTeamsChannels = Entity & {
  spec: {
    teamsChannels?: {
      title: string;
      url: string;
    }[];
  };
};

/** @public */
export const isTeamsChannelsEntity = (
  value: unknown,
): value is HasIPenTeamsChannels =>
  hasSpec(value) && 'teamsChannels' in value.spec;

/** @public */
export type GuardedType<TGuard> = TGuard extends (
  value: unknown,
) => value is infer TEntity
  ? TEntity
  : never;

/** @public */
export type RequiredNotNull<T> = {
  [P in keyof T]-?: NonNullable<T[P]>;
};

/** @public */
export type WithRequiredFields<T, K extends keyof T> = RequiredNotNull<Pick<T, K>>
  & T;

export const isTemplateEntity = (entity: Entity): entity is TemplateV1beta3 =>
  entity.kind === 'Template';

export type LeanixEntity = Entity & {
  spec: {
    leanix: {
      aplElementId: string;
      factsheetUrl?: string;
    };
  };
};

export const isLeanixEntity = (value: unknown): value is LeanixEntity => {
  if (!hasSpec(value)) return false;
  const leanix = (value.spec as Record<string, unknown>).leanix;
  return (
    isObject(leanix)
    && typeof (leanix as Record<string, unknown>).aplElementId === 'string'
  );
};

export const isDummyDomainEntity = (value: unknown): value is DomainEntity =>
  isDomainEntity(value) && value.spec.type === 'dummy';

export const isIPenPlatformDomainEntity = (entity: unknown) =>
  isRootDomainEntity(entity) && entity.metadata.name === 'ipen-domain';

export const isEnergyNetworkDomainEntity = (entity: unknown) =>
  isRootDomainEntity(entity)
  && entity.metadata.name === 'energy-network-domain';

export type TypedGetEntitiesRequest<T extends Entity> = {
  after?: string;
  fields?: EntityFilterKeys<T>;
  filter: EntityFilter<T> | EntityFilter<T>[];
  limit?: number;
  offset?: number;
  order?:
    | {
      field: EntityFilterKeys<T>;
      order: 'asc' | 'desc';
    }
    | {
      field: EntityFilterKeys<T>;
      order: 'asc' | 'desc';
    }[];
  predicate?: (entity: Entity) => entity is T;
};

export * from './types/LooseString';
